import { createSlice } from '@reduxjs/toolkit'

import { init, syncBehaviour } from '../classState/studentsSlice'
import { init as canvasInit } from '../classCanvas/canvasSlice'
import { setImpulseButtonText } from '../behaviourControls/behaviourControlsSlice'
import { setWeather } from '../SessionSettings/WeatherSlice';
import { getIsEnabledGlobal } from '../ConfigurationContent/RoomModuleUtils'
import { sendRoomModuleParameters } from '../ConfigurationContent/ConfigureRoomModulesSlice'
import { setThemes } from '../SessionSettings/ThemeSlice'
import { requestThemes } from '../SessionSettings/ThemeSelectionPanel'
import { setScenarios } from '../SessionSettings/ScenarioSlice'
import { requestScenarios } from '../SessionSettings/ScenarioSelectionPanel'
import { setScriptInfoList, updateScriptLog } from '../SessionSettings/DataCollectionSlice'
import { sendConfigurationParameters, sendDataCollectionParameters, sendSpeechtoolsParameters, setNoiseLevel } from '../ConfigurationContent/ConfigurationOptionsSlice'

 
export const websocketSlice = createSlice({
    name: 'websocket',
    initialState: {
        status: 'closed', // TODO other interesting states?
        error: '',
        connecting: false,
        availableHandlers: {},
        controllers: []
    },
    reducers: {
        connectionState: (state, action) => ({ ...state, status: action.payload, connecting: false }),
        socketError: (state, action) => ({ ...state, error: action.payload }),
        tryConnect: (state, action) => ({ ...state, connecting: true }),
        setAvailableHandlers: (state, action) => {
            console.log("Setting available handlers:", action.payload);
            return { ...state, availableHandlers: action.payload };
        },
        setControllers: (state, action) => {
            console.log("Received controllers:", action.payload);
            return { ...state, controllers: action.payload };
        }
    }
})

export const { connectionState, socketError, tryConnect, setAvailableHandlers, setControllers } = websocketSlice.actions

let socket

/**
 * Sends a clear-text message to the socket.
 * @param {string} type message type/key
 * @param {string} message payload
 */
export const emitMsg = (type, message) => {
    console.log(`[socket] emit message of type ${type} with payload:`, message);
    socket.send(`${type};${message}`);
};

/**
 * Stringifies the payload object before sending it.
 * @param {string} type 
 * @param {object} payload
 */
export const emitJson = (type, payload) => {
    console.log(`[socket] emit JSON of type ${type} with payload:`, payload);
    emitMsg(type, JSON.stringify(payload));
}
/**
 * Stringifies a redux action before sending it.
 * @param {object} action 
 */
export const emit = action => emitJson(action.type, { ...action, type: undefined })

export const requestBootstrapping = () => {
    emit({ type: 'bootstrap' })
}

export const requestNextSlide = () => {
    emit({ type: 'presentation' })
}

export const requestControllerAvailability = () => {
    emit({ type: 'askForControllerAvailability' })
}

export const requestDataCollectionScriptList = () => {
    emit({ type: 'requestScriptInfoList' })
}

class Teacher {
  Teacher() {
    this.pos = undefined
  }

  setTeacherPos(pos) {
    this.pos = pos
  }

  getTeacherPos() {
    return this.pos
  }
}

// Use class instead of redux to drastically reduce amount of actions
export const teacher = new Teacher()

const messageHandlers = dispatch => ({
    bootstrap: payload => { dispatch(init(payload)); dispatch(canvasInit(payload.students)) },
    behave: student => dispatch(syncBehaviour(student)),
    syncTeacher: pos => teacher.setTeacherPos(pos),
    setImpulseButtonText: payload => {
        console.log('setImpulseButtonText received with payload:', payload);
        dispatch(setImpulseButtonText(payload))
    },
    checkFunctions: (payload) => {
        const results = checkFunctionsExist(payload.functions);
        console.log('checkFunctions: ', payload.functions, ' result: ', results);
        emitJson('functionCheckResult', results);
    },
    receiveControllerAvailability: controllers => {
        dispatch(setControllers(controllers));
    },
    setWeatherByUnity: (payload) => {
        const { command } = payload;
        
        if (!['sunny', 'raining', 'snowing'].includes(command)) {
            console.error('Unknown weather command:', command);
        }

        dispatch(setWeather(command));
    },
    setNoiseByUnity: (payload) => {
        dispatch(setNoiseLevel(payload))
    },
    setThemeNamesByUnity: (payload) => {
        dispatch(setThemes(payload));
    },
    setScenarioNamesByUnity: (payload) => {
        dispatch(setScenarios(payload));
    },
    sendScriptInfoList: (payload) => {
        let parsedPayload;
        try {
            parsedPayload = typeof payload === "string" ? JSON.parse(payload) : payload;

            if (!Array.isArray(parsedPayload)) {
                console.error("Parsed payload is not an array:", parsedPayload);
                return;
            }
        } catch (error) {
            console.error("Error parsing scriptInfoList payload:", error);
            return;
        }

        // Grouping by alias, only one entry per alias (e.g. controller)
        const groupedScriptInfoList = Object.values(
            parsedPayload.reduce((grouped, scriptInfo) => {
                if (!grouped[scriptInfo.scriptAlias]) {
                    grouped[scriptInfo.scriptAlias] = { ...scriptInfo };
                } else {
                    grouped[scriptInfo.scriptAlias].scriptAvailable =
                        grouped[scriptInfo.scriptAlias].scriptAvailable || scriptInfo.scriptAvailable;
                }
                return grouped;
            }, {})
        );

        dispatch(setScriptInfoList(groupedScriptInfoList));
        dispatch(sendDataCollectionParameters());
    },
    updateScriptInfoEntry: (payload) => {
        const parsedPayload = typeof payload === "string" ? JSON.parse(payload) : payload;
        dispatch(updateScriptLog(parsedPayload));
    }
});

const handleMessage = (action, dispatch) => {
    const handler = messageHandlers(dispatch)[action.type]

    if (handler) {
        handler(action.payload)
    } else {
        dispatch(socketError('Unknown action type ' + action.type))
        dispatch(connectionState('warning'))
    }
}

export const initSocket = (retry = false) => dispatch => {
    if (socket && !retry) return // Don't run this twice

    socket = new WebSocket("ws://localhost:10000/SockServer")
    dispatch(tryConnect())

    socket.onopen = e => {
        console.log("[socket] Connection established!")
        dispatch(connectionState('connected'))

        const isEnabled = getIsEnabledGlobal();
        console.log("Room Module Enabled:", isEnabled);
        if (isEnabled) {
            dispatch(sendRoomModuleParameters());
        }
        else {
            requestBootstrapping();
            requestDataCollectionScriptList();
            dispatch(sendConfigurationParameters());
            dispatch(sendSpeechtoolsParameters());
            requestThemes();
            requestScenarios();
        }

        // Check available handlers after connection
        const availableHandlers = checkFunctionsExist(Object.keys(messageHandlers(dispatch)))
        dispatch(setAvailableHandlers(availableHandlers))

        // Ask unity for controllers (non-existent or deactivated controllers lead
        // to a deactivation of the functionality)
        requestControllerAvailability()
    }

    socket.onmessage = e => {

        const action = JSON.parse(e.data)

        if (action.type === 'syncTeacher') {
            console.log("[DEBUG] Received message: syncTeacher [shortened]");
        }
        else {
            console.log("[DEBUG] Received message:", e.data);
        }

        handleMessage(action, dispatch)
    }

    socket.onerror = e => {
        dispatch(connectionState('error'))
        dispatch(socketError(e.message))
    }

    socket.onclose = e => {
        dispatch(connectionState('closed'))
    }
}

/**
 * Checks if the requested functions exist in the message handlers.
 * @param {Array} requestedFunctions - List of requested function names.
 * @returns {Object} - Object indicating whether each function exists.
 */
export const checkFunctionsExist = (requestedFunctions) => {
    const handlers = Object.keys(messageHandlers(() => { }));
    return requestedFunctions.reduce((acc, func) => {
        acc[func] = handlers.includes(func);
        return acc;
    }, {});
};

export const statusSelector = ({ websocket }) => websocket.status
export const errorSelector = ({ websocket }) => websocket.error
export const isConnectingSelector = ({ websocket }) => websocket.connecting
export const availableHandlersSelector = ({ websocket }) => websocket.availableHandlers
export const controllersSelector = ({ websocket }) => websocket.controllers


export default websocketSlice.reducer
